// v3 context
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import {
  doc,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../firebase';

const AdVocalizeContext = createContext();

export const appId = 'advocalize-global-v1';

// Voices confirmed working on both Vertex AI (gemini-2.5-flash-tts) and AI Studio TTS models.
// Labels are user-facing style descriptors — API voice names are mapped in the backend.
export const VOICES = [
  { name: 'Aoede',     label: 'Warm',        gender: 'female', style: 'warm' },
  { name: 'Achernar',  label: 'Soft',        gender: 'female', style: 'soft' },
  { name: 'Laomedeia', label: 'Upbeat',      gender: 'female', style: 'upbeat' },
  { name: 'Charon',    label: 'Bold',        gender: 'male',   style: 'bold' },
  { name: 'Fenrir',    label: 'Energetic',   gender: 'male',   style: 'energetic' },
  { name: 'Orus',      label: 'Professional',gender: 'male',   style: 'professional' },
];

function pcmBytesToWav(pcmBytes, sampleRate = 24000, numChannels = 1, bitsPerSample = 16) {
  const dataLen = pcmBytes.length;
  const buf = new ArrayBuffer(44 + dataLen);
  const v = new DataView(buf);
  const ws = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  ws(0, 'RIFF'); v.setUint32(4, 36 + dataLen, true); ws(8, 'WAVE');
  ws(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true);
  v.setUint16(22, numChannels, true); v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true);
  v.setUint16(32, numChannels * (bitsPerSample / 8), true); v.setUint16(34, bitsPerSample, true);
  ws(36, 'data'); v.setUint32(40, dataLen, true);
  new Uint8Array(buf, 44).set(pcmBytes);
  return buf;
}

export const TONES = [
  { id: 'professional', label: 'Professional' },
  { id: 'excited',      label: 'Excited' },
  { id: 'friendly',     label: 'Friendly' },
  { id: 'urgent',       label: 'Urgent' },
  { id: 'luxury',       label: 'Luxury' },
  { id: 'playful',      label: 'Playful' },
];

// ── API Keychain helpers (per-user, UID-scoped) ──────────────────────────────
const kcKey    = (uid) => uid ? `av_api_keychain_${uid}` : 'av_api_keychain';
const kcActive = (uid) => uid ? `av_active_key_id_${uid}` : 'av_active_key_id';
const genKeyId = () => `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

const loadKeychain = (uid) => {
  try {
    let chain = JSON.parse(localStorage.getItem(kcKey(uid)) || '[]');
    if (chain.length === 0 && uid) {
      // One-time migration: device-level chain or legacy single key → this user's scoped chain
      const legacyChain = JSON.parse(localStorage.getItem('av_api_keychain') || '[]');
      const legacyGemini = localStorage.getItem('gemini_api_key');
      if (legacyChain.length > 0) {
        chain = legacyChain;
        localStorage.setItem(kcKey(uid), JSON.stringify(chain));
      } else if (legacyGemini?.trim()) {
        const id = genKeyId();
        chain = [{ id, label: 'My API Key', value: legacyGemini.trim(), status: 'unknown', latencyMs: null }];
        localStorage.setItem(kcKey(uid), JSON.stringify(chain));
        localStorage.setItem(kcActive(uid), id);
      }
    }
    const activeId = localStorage.getItem(kcActive(uid)) || chain[0]?.id || null;
    return { chain, activeId, activeValue: chain.find(k => k.id === activeId)?.value || '' };
  } catch { return { chain: [], activeId: null, activeValue: '' }; }
};

const saveChain = (chain, uid) => {
  try { localStorage.setItem(kcKey(uid), JSON.stringify(chain)); } catch {}
};
// ────────────────────────────────────────────────────────────────────────────

export const AdVocalizeProvider = ({ children }) => {
  // --- Auth & Usage ---
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState({ creditsRemaining: 0, tier: 'free' });

  // --- API Keychain (loaded per-user on auth state change) ---
  const [apiKeys, setApiKeys]           = useState([]);
  const [activeKeyId, setActiveKeyId]   = useState(null);
  const [localKey, setLocalKeyDirect]   = useState('');
  const [preferredEngine, setPreferredEngine] = useState('managed'); // 'managed' | 'byok'
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [redeemPhase, setRedeemPhase] = useState('code'); // 'code' | 'auth'
  const [pendingCode, setPendingCode] = useState('');
  
  // --- Studio Flow ---
  const [step, setStep] = useState(0); 
  const [image, setImage] = useState(null);
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [imgTransform, setImgTransform] = useState({ x: 0, y: 0, scale: 1, rotate: 0 });
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [fitMode, setFitMode] = useState('cover');
  
  // --- AI / Creative State ---
  const [text, setText] = useState("");
  const [instruction, setInstruction] = useState("");
  const [scriptDuration, setScriptDuration] = useState('30s');
  const [selectedVoice, setSelectedVoice] = useState(VOICES[0].name);
  const [selectedTone, setSelectedTone] = useState('professional');
  const [history, setHistory] = useState([]); 
  const [currentTripletIndex, setCurrentTripletIndex] = useState(-1);
  const [activeTakeId, setActiveTakeId] = useState('pulse');
  
  // --- Analysis Artifacts ---
  const [primaryMemory, setPrimaryMemory] = useState(null);
  const [adDirective, setAdDirective] = useState(null);
  const [energyShape, setEnergyShape] = useState(null);
  const [pausePoints, setPausePoints] = useState(null);
  const [sessionLanguage, setSessionLanguage] = useState(null);
  
  // --- Execution State ---
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [analyzePhase, setAnalyzePhase] = useState(null);
  const [audioTakes, setAudioTakes] = useState([]);
  const [selectedTakeIdx, setSelectedTakeIdx] = useState(0);
  const [isCreatingVideo, setIsCreatingVideo] = useState(false);
  const [finalVideoUrl, setFinalVideoUrl] = useState(null);
  const [localVoiceCount, setLocalVoiceCount] = useState(0);

  const navigatedRef = useRef(false);
  const pendingCodeRef = useRef('');
  const switchingAccountsRef = useRef(false);
  const uidRef = useRef(null);
  const prevUidRef = useRef(null);

  // Derived — which key actually gets sent to the backend
  const effectiveKey = preferredEngine === 'byok' ? localKey : '';

  // --- Effects ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      if (!u || u.isAnonymous) {
        setUsage({ creditsRemaining: 0, tier: 'free' });
        setUser(null);
        setApiKeys([]); setActiveKeyId(null); setLocalKeyDirect('');
        setPreferredEngine('managed');
        setAuthError('');
        uidRef.current = null;
        navigatedRef.current = false;
        if (!pendingCodeRef.current && !switchingAccountsRef.current) setRedeemPhase('code');
        switchingAccountsRef.current = false;
        return;
      }
      // Different UID → account switch: reset step, keep media
      if (prevUidRef.current && prevUidRef.current !== u.uid) setStep(0);
      prevUidRef.current = u.uid;
      uidRef.current = u.uid;
      // Load this user's UID-scoped keychain
      const kc = loadKeychain(u.uid);
      setApiKeys(kc.chain);
      setActiveKeyId(kc.activeId);
      setLocalKeyDirect(kc.activeValue);
      setPreferredEngine(kc.activeValue ? 'byok' : 'managed');
      navigatedRef.current = false;
      setUser(u);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const unsubscribe = onSnapshot(
      doc(db, 'artifacts', appId, 'users', user.uid, 'usage', 'stats'),
      (s) => {
        const data = s.exists() ? s.data() : { creditsRemaining: 0, tier: 'free' };
        setUsage(data);
      },
      () => { /* error handling */ }
    );
    return () => unsubscribe();
  }, [user]);

  // --- Actions ---
  const handleGoogleAuth = async () => {
    setIsAuthLoading(true);
    setAuthError('');
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      if (pendingCode) await handleRedeem(pendingCode);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleEmailAuth = async (email, password, mode) => {
    setIsAuthLoading(true);
    setAuthError('');
    try {
      if (mode === 'signup') {
        await createUserWithEmailAndPassword(auth, email.trim(), password);
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      }
      if (pendingCode) await handleRedeem(pendingCode);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleRedeem = async (code) => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setIsAuthLoading(true); setAuthError('');
    try {
      const redeemFn = httpsCallable(functions, 'redeemAppSumoCodeV1');
      const result = await redeemFn({ code: c });
      if (result.data.success) {
        setAuthMessage(result.data.alreadyOwned ? 'Welcome back!' : 'Studio Activated!');
        setPreferredEngine('managed'); // new code → snap back to Managed engine
        setPendingCode('');
        pendingCodeRef.current = '';
        setTimeout(() => setAuthMessage(''), 2000);
        return true;
      } else {
        throw new Error(result.data.message || 'Invalid Key');
      }
    } catch (e) {
      setAuthError(e.message);
      return false;
    } finally { setIsAuthLoading(false); }
  };

  const runMasterPipeline = async (imgData) => {
    setAnalyzePhase('AUDITING BRAND...');
    try {
      const analyzeFn = httpsCallable(functions, 'analyzeImage');
      const vr = await analyzeFn({ imageBase64: imgData, userApiKey: effectiveKey, aspectRatio });
      const { primaryMemory: pm, adDirective: ad, energyShape: es, pausePoints: pp, language: lang, themes } = vr.data;

      setPrimaryMemory({ ...pm, themes } || null);
      setAdDirective(ad || null);
      setEnergyShape(es || null);
      setPausePoints(pp || null);
      setSessionLanguage(lang || null);

      setAnalyzePhase('WRITING TRIPLETS...');
      const scriptFn = httpsCallable(functions, 'generateScript');
      const sr = await scriptFn({
        prompt: pm?.brandName || 'this product',
        primaryMemory: pm,
        adDirective: ad,
        energyShape: es,
        pausePoints: pp,
        language: lang,
        tone: selectedTone,
        userApiKey: effectiveKey,
        duration: scriptDuration,
        aspectRatio
      });

      if (sr.data.success) {
        const firstTriplet = sr.data.takes;
        setHistory([firstTriplet]);
        setCurrentTripletIndex(0);
        setText(firstTriplet.find(t => t.id === activeTakeId)?.text || firstTriplet[0].text);
      } else {
        throw new Error(sr.data.message || "Script generation failed.");
      }
    } catch (e) { 
        setAuthError(e.message || "Creative Engine is busy. Try again."); 
    } finally { 
        setAnalyzePhase(null); 
    }
  };

  const refineScript = async (customInstruction) => {
    const instr = customInstruction || instruction;
    if (!instr.trim() || history.length >= 10) return;
    setIsRefining(true); setAuthError("");
    try {
      const currentTakeText = history[currentTripletIndex].find(t => t.id === activeTakeId).text;
      const scriptFn = httpsCallable(functions, 'generateScript');
      const result = await scriptFn({
        prompt: currentTakeText,
        userInstruction: instr,
        userApiKey: effectiveKey,
        language: sessionLanguage,
        tone: selectedTone,
        primaryMemory,
        adDirective,
        energyShape,
        pausePoints,
        duration: scriptDuration,
        aspectRatio,
        history: history.slice(-3).map(triplet => {
          const t = triplet.find(take => take.id === activeTakeId) || triplet[0];
          return t.text;
        }),
      });
      
      if (result.data.success) {
        const nextTriplet = result.data.takes;
        const newHistory = [...history, nextTriplet];
        setHistory(newHistory);
        setCurrentTripletIndex(newHistory.length - 1);
        setText(nextTriplet.find(t => t.id === activeTakeId)?.text || nextTriplet[0].text);
        setInstruction("");
      }
    } catch (e) { setAuthError("Director is busy."); } finally { setIsRefining(false); }
  };

  const generateAudio = async () => {
    if (!text.trim()) return;
    if (!effectiveKey && localVoiceCount >= 10) {
      setAuthError("Session limit reached — 10/10 takes used. Start a new project to continue.");
      return;
    }
    setIsGenerating(true); setAuthError("");
    try {
      const generateVoiceFn = httpsCallable(functions, 'generateVoice');
      const result = await generateVoiceFn({
        text: text.trim(),
        voiceName: selectedVoice,
        tone: selectedTone,
        userApiKey: effectiveKey,
        adDirective,
        energyShape,
        pausePoints,
      });
      const { audioBase64, mimeType: rawMime } = result.data;
      if (!audioBase64) throw new Error("No audio returned.");
      const binaryString = atob(audioBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
      const mime = rawMime || 'audio/wav';
      let blob;
      if (mime.startsWith('audio/L16') || mime.startsWith('audio/pcm') || mime.startsWith('audio/raw')) {
        const rateMatch = mime.match(/rate=(\d+)/);
        blob = new Blob([pcmBytesToWav(bytes, rateMatch ? parseInt(rateMatch[1], 10) : 24000)], { type: 'audio/wav' });
      } else {
        blob = new Blob([bytes.buffer], { type: mime });
      }
      const url = URL.createObjectURL(blob);
      setAudioTakes(prev => [{ url, blob, voiceName: selectedVoice, voiceLabel: VOICES.find(v => v.name === selectedVoice)?.label || '', scriptType: activeTakeId }, ...prev]);
      setSelectedTakeIdx(0);
      setLocalVoiceCount(prev => prev + 1);
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('BYOK_INVALID_KEY'))
        setAuthError('API key rejected — verify it in Settings ⚙');
      else if (msg.includes('BYOK_QUOTA_EXCEEDED'))
        setAuthError('AI Studio free quota hit. Wait ~1 min, then try again.');
      else if (msg.includes('BYOK_NO_MODEL_AVAILABLE'))
        setAuthError('No TTS model available on this key — check AI Studio access.');
      else
        setAuthError(`Voice error: ${msg}`);
    } finally { setIsGenerating(false); }
  };

  // --- Keychain actions ---
  const addApiKey = (label, value) => {
    const id = genKeyId();
    const key = { id, label: label?.trim() || 'API Key', value: value.trim(), status: 'unknown', latencyMs: null };
    setApiKeys(prev => { const u = [...prev, key]; saveChain(u, uidRef.current); return u; });
    return id;
  };

  const removeApiKey = (id) => {
    setApiKeys(prev => {
      const u = prev.filter(k => k.id !== id);
      saveChain(u, uidRef.current);
      if (activeKeyId === id) {
        const next = u[0] || null;
        setActiveKeyId(next?.id || null);
        try { localStorage.setItem(kcActive(uidRef.current), next?.id || ''); } catch {}
        setLocalKeyDirect(next?.value || '');
        if (!next?.value) setPreferredEngine('managed');
      }
      return u;
    });
  };

  const selectApiKey = (id) => {
    setApiKeys(prev => {
      const key = prev.find(k => k.id === id);
      if (!key) return prev;
      setActiveKeyId(id);
      try { localStorage.setItem(kcActive(uidRef.current), id); } catch {}
      setLocalKeyDirect(key.value);
      return prev;
    });
  };

  const updateApiKeyMeta = (id, changes) => {
    setApiKeys(prev => {
      const u = prev.map(k => k.id === id ? { ...k, ...changes } : k);
      saveChain(u, uidRef.current);
      if (changes.value !== undefined && id === activeKeyId) setLocalKeyDirect(changes.value);
      return u;
    });
  };

  const testApiKey = async (id) => {
    const key = apiKeys.find(k => k.id === id);
    if (!key?.value) return;
    updateApiKeyMeta(id, { status: 'checking', latencyMs: null });
    try {
      const checkFn = httpsCallable(functions, 'checkStudioHealth');
      const result = await checkFn({ userApiKey: key.value });
      const b = result.data.byok;
      updateApiKeyMeta(id, { status: b?.status || 'error', latencyMs: b?.latencyMs || null });
    } catch {
      updateApiKeyMeta(id, { status: 'error', latencyMs: null });
    }
  };

  const resetStudio = () => {
    setStep(0);
    setImage(null);
    setHistory([]);
    setText("");
    setAudioTakes([]);
    setSelectedTakeIdx(0);
    setLocalVoiceCount(0);
    setFinalVideoUrl(null);
    setAnalyzePhase(null);
  };

  const value = {
    // State
    user, usage, localKey, isAuthLoading, authError, authMessage, redeemPhase, pendingCode,
    step, image, aspectRatio, imgTransform, selectedFilter, fitMode,
    text, instruction, scriptDuration, selectedVoice, selectedTone, history, currentTripletIndex, activeTakeId,
    primaryMemory, adDirective, energyShape, pausePoints, sessionLanguage,
    isGenerating, isRefining, analyzePhase, audioTakes, selectedTakeIdx, isCreatingVideo, finalVideoUrl, localVoiceCount,

    // Keychain + engine
    apiKeys, activeKeyId, preferredEngine, setPreferredEngine, effectiveKey,
    addApiKey, removeApiKey, selectApiKey, updateApiKeyMeta, testApiKey,

    // Setters
    setStep, setImage, setAspectRatio, setImgTransform, setSelectedFilter, setFitMode,
    setText, setInstruction, setScriptDuration, setSelectedVoice, setSelectedTone, setHistory, setCurrentTripletIndex, setActiveTakeId,
    setAuthError, setRedeemPhase, setPendingCode, setSelectedTakeIdx,

    // Actions
    handleGoogleAuth, handleEmailAuth, handleRedeem,
    runMasterPipeline, refineScript, generateAudio, resetStudio,
    fulfillExport: async () => {
      if (effectiveKey) return; // BYOK users are unlimited
      const fn = httpsCallable(functions, 'fulfillAudioExportV1');
      await fn(); // throws HttpsError if 0 credits
    },
    runDiagnostic: async () => {
      const checkFn = httpsCallable(functions, 'checkStudioHealth');
      const result = await checkFn({ userApiKey: effectiveKey });
      return result.data;
    },
    signOut: async () => {
      switchingAccountsRef.current = true;
      setRedeemPhase('auth');
      setAuthError('');
      await signOut(auth);
    }
  };

  return (
    <AdVocalizeContext.Provider value={value}>
      {children}
    </AdVocalizeContext.Provider>
  );
};

export const useAdVocalizeCore = () => {
  const context = useContext(AdVocalizeContext);
  if (!context) throw new Error('useAdVocalizeCore must be used within AdVocalizeProvider');
  return context;
};
